import React, { useState, useEffect } from 'react';
import { Card, Typography, Select, DatePicker, Space, Tag, Tooltip, Spin, Button, Modal, Descriptions, message } from 'antd';
import { CalendarOutlined, ReloadOutlined, ClockCircleOutlined, UserOutlined, FilePdfOutlined, DownloadOutlined, LinkOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { getPlanning, downloadFicheEquipe } from '../../services/adminService';

const { Title, Text } = Typography;

const PlanningPage = () => {
  const [planning, setPlanning] = useState([]);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupeFilter, setGroupeFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [downloadingTeam, setDownloadingTeam] = useState(null);
  const [mapTeam, setMapTeam] = useState(null);

  const fetchPlanning = async () => {
    setLoading(true);
    try {
      const params = {};
      if (groupeFilter) params.groupe = groupeFilter;
      if (dateRange) {
        params.date_debut = dateRange[0].format('YYYY-MM-DD');
        params.date_fin = dateRange[1].format('YYYY-MM-DD');
      }
      const data = await getPlanning(params);
      setPlanning(data.planning || []);
      setDates(data.dates || []);
    } catch (err) {
      console.error('Error fetching planning:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPlanning(); }, [groupeFilter, dateRange]);

  const handleDownloadFiche = async (team) => {
    const dateDebut = dates[0];
    const dateFin = dates[dates.length - 1];
    if (!dateDebut || !dateFin) {
      message.warning('Aucune période sélectionnée');
      return;
    }
    setDownloadingTeam(team.id);
    try {
      const blob = await downloadFicheEquipe(team.id, dateDebut, dateFin);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fiches_${team.nom.replace(/\s+/g, '_')}_${dateDebut}_${dateFin}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success(`Fiche ${team.nom} téléchargée`);
    } catch (err) {
      const errorMsg = err.response?.status === 404 ? 'Aucun RDV trouvé pour cette équipe sur cette période' : 'Erreur lors de la génération du PDF';
      message.error(errorMsg);
    } finally {
      setDownloadingTeam(null);
    }
  };

  const handleOpenMap = (team) => {
    // Collect all RDV addresses for this team across displayed dates
    const rdvList = [];
    for (const date of dates) {
      const jour = team.jours?.[date];
      if (jour && jour.rdv) {
        jour.rdv.forEach(rdv => {
          if (rdv.address) rdvList.push(rdv);
        });
      }
    }
    if (rdvList.length === 0) {
      message.warning('Aucun RDV avec adresse pour cette équipe');
      return;
    }
    setMapTeam({ team, rdvList });
  };

  const formatDateHeader = (dateStr) => {
    const d = new Date(dateStr);
    const dow = d.toLocaleDateString('fr-FR', { weekday: 'short' }).toUpperCase();
    const day = d.getDate();
    const month = d.toLocaleDateString('fr-FR', { month: 'short' });
    return { dow, day, month };
  };

  const renderCell = (team, date) => {
    const jour = team.jours?.[date];
    if (!jour) return null;

    if (jour.absent) {
      return (
        <div style={{ background: '#fef2f2', borderRadius: 6, padding: '4px 8px', textAlign: 'center' }}>
          <Text type="danger" style={{ fontSize: 11 }}>Absent</Text>
          {jour.absence_motif && (
            <div><Text type="secondary" style={{ fontSize: 10 }}>{jour.absence_motif}</Text></div>
          )}
        </div>
      );
    }

    if (jour.nb_rdv === 0) {
      return <Text type="secondary" style={{ fontSize: 11 }}>Disponible</Text>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {jour.rdv.map((rdv, i) => (
          <Tooltip key={i} title={`${rdv.heure} - CP: ${rdv.cp || '?'} - ${rdv.firstname || ''} ${rdv.lastname || ''}`}>
            <div
              onClick={() => setSelectedRdv({ ...rdv, equipe: team.nom, date })}
              style={{
                background: team.couleur + '20',
                borderLeft: `3px solid ${team.couleur}`,
                borderRadius: 4, padding: '2px 6px', cursor: 'pointer',
                fontSize: 11, lineHeight: 1.4,
              }}
            >
              <strong>{rdv.heure}</strong>
              {rdv.cp && <span style={{ marginLeft: 4, opacity: 0.7 }}>{rdv.cp}</span>}
            </div>
          </Tooltip>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Planning</Title>
        <Space>
          <Select
            placeholder="Groupe"
            allowClear
            style={{ width: 160 }}
            onChange={setGroupeFilter}
            options={[
              { value: 'BASE', label: 'Base' },
              { value: 'SUPPLEMENTAIRE', label: 'Supplémentaire' },
              { value: 'SAV', label: 'SAV' },
            ]}
          />
          <DatePicker.RangePicker
            onChange={setDateRange}
            format="DD/MM/YYYY"
          />
          <Button icon={<ReloadOutlined />} onClick={fetchPlanning}>Actualiser</Button>
        </Space>
      </div>

      {loading ? (
        <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />
      ) : (
        <Card styles={{ body: { padding: 0, overflow: 'auto' } }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', minWidth: 140, position: 'sticky', left: 0, background: '#fafafa', zIndex: 1 }}>
                  Équipe
                </th>
                {dates.map(date => {
                  const { dow, day, month } = formatDateHeader(date);
                  const isToday = date === new Date().toISOString().split('T')[0];
                  return (
                    <th key={date} style={{
                      padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0',
                      minWidth: 120, background: isToday ? '#ecfdf5' : '#fafafa',
                    }}>
                      <div style={{ fontSize: 10, opacity: 0.6 }}>{dow}</div>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{day}</div>
                      <div style={{ fontSize: 10, opacity: 0.6 }}>{month}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {planning.map(team => (
                <tr key={team.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{
                    padding: '8px 12px', position: 'sticky', left: 0,
                    background: 'white', zIndex: 1, borderRight: '1px solid #f0f0f0',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: team.couleur, flexShrink: 0 }} />
                      <Text strong style={{ fontSize: 12, flex: 1 }}>{team.nom}</Text>
                      <Tooltip title="Carte des RDV">
                        <Button
                          type="text"
                          size="small"
                          icon={<EnvironmentOutlined />}
                          onClick={() => handleOpenMap(team)}
                          style={{ color: '#2563eb', fontSize: 14 }}
                        />
                      </Tooltip>
                      <Tooltip title="Télécharger les fiches">
                        <Button
                          type="text"
                          size="small"
                          icon={<FilePdfOutlined />}
                          loading={downloadingTeam === team.id}
                          onClick={() => handleDownloadFiche(team)}
                          style={{ color: '#059669', fontSize: 14 }}
                        />
                      </Tooltip>
                    </div>
                  </td>
                  {dates.map(date => (
                    <td key={date} style={{
                      padding: '6px 8px', verticalAlign: 'top', borderRight: '1px solid #f5f5f5',
                      background: date === new Date().toISOString().split('T')[0] ? '#f8fdf9' : 'white',
                    }}>
                      {renderCell(team, date)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        title={mapTeam ? `Carte des RDV - ${mapTeam.team.nom}` : ''}
        open={!!mapTeam}
        onCancel={() => setMapTeam(null)}
        footer={
          mapTeam && (
            <Button
              type="primary"
              icon={<EnvironmentOutlined />}
              onClick={() => {
                const pts = mapTeam.rdvList.map(r => r.address);
                const origin = encodeURIComponent(pts[0]);
                const dest = encodeURIComponent(pts[pts.length - 1]);
                const waypoints = pts.length > 2 ? '&waypoints=' + pts.slice(1, -1).map(p => encodeURIComponent(p)).join('|') : '';
                window.open(`https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${waypoints}&travelmode=driving`, '_blank');
              }}
              style={{ background: '#059669' }}
            >
              Ouvrir l'itinéraire dans Google Maps
            </Button>
          )
        }
        width={600}
      >
        {mapTeam && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {mapTeam.rdvList.map((rdv, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  background: '#f8fafc', borderRadius: 8, borderLeft: `4px solid ${mapTeam.team.couleur}`
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: mapTeam.team.couleur,
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, flexShrink: 0
                  }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{rdv.firstname} {rdv.lastname}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{rdv.address}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: '#059669' }}>{rdv.heure}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{rdv.cp}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Détail du rendez-vous"
        open={!!selectedRdv}
        onCancel={() => setSelectedRdv(null)}
        footer={null}
      >
        {selectedRdv && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={<><CalendarOutlined /> Date</>}>{selectedRdv.date}</Descriptions.Item>
            <Descriptions.Item label={<><ClockCircleOutlined /> Heure</>}>{selectedRdv.heure}</Descriptions.Item>
            <Descriptions.Item label="Équipe">{selectedRdv.equipe}</Descriptions.Item>
            <Descriptions.Item label={<><UserOutlined /> Client</>}>{selectedRdv.firstname} {selectedRdv.lastname}</Descriptions.Item>
            <Descriptions.Item label="CP">{selectedRdv.cp}</Descriptions.Item>
            <Descriptions.Item label="Surface">{selectedRdv.surface ? `${selectedRdv.surface} m²` : '-'}</Descriptions.Item>
            <Descriptions.Item label="Adresse">{selectedRdv.address || '-'}</Descriptions.Item>
            <Descriptions.Item label="Statut"><Tag color="green">{selectedRdv.status}</Tag></Descriptions.Item>
            <Descriptions.Item label={<><LinkOutlined /> Fiche CRM</>}>
              <a href={`https://kortex-esp.phone.gs/vtigercrm/index.php?module=Leads&view=Detail&record=${selectedRdv.id}&app=MARKETING`} target="_blank" rel="noopener noreferrer">
                Ouvrir dans vtiger
              </a>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default PlanningPage;
